import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { VenueResearchService, type VenueResearch } from '@/services/VenueResearchService';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { Select } from '@/components/ui/Select';
import { Eye, MapPin, Building2, Users, Star, Clock, DollarSign, Repeat, Ban } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

interface ResearchHistoryTableProps {
  onViewDetails?: (research: VenueResearch) => void;
}

/**
 * Get status badge variant based on research status
 */
function getStatusBadge(status: VenueResearch['status']): { variant: 'default' | 'secondary' | 'success' | 'warning' | 'error'; label: string } {
  switch (status) {
    case 'pending':
      return { variant: 'secondary', label: 'Pending' };
    case 'processing':
      return { variant: 'warning', label: 'Processing' };
    case 'completed':
      return { variant: 'success', label: 'Completed' };
    case 'failed':
      return { variant: 'error', label: 'Failed' };
    case 'cancelled':
      return { variant: 'default', label: 'Cancelled' };
    default:
      return { variant: 'default', label: String(status) };
  }
}

/**
 * Format duration in seconds to human-readable string
 */
function formatDuration(seconds: number | null): string {
  if (seconds === null) return '-';
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}m ${remainingSeconds}s`;
}

/**
 * Format search criteria into readable text
 */
function formatCriteria(research: VenueResearch): React.ReactNode {
  const criteria: React.ReactNode[] = [];
  
  if (research.min_rooms) {
    criteria.push(
      <span key="rooms" className="inline-flex items-center gap-1 text-sm text-gray-600">
        <Building2 className="w-3 h-3" />
        {research.min_rooms}+ rooms
      </span>
    );
  }
  
  if (research.min_conference_capacity) {
    criteria.push(
      <span key="capacity" className="inline-flex items-center gap-1 text-sm text-gray-600">
        <Users className="w-3 h-3" />
        {research.min_conference_capacity}+ capacity
      </span>
    );
  }
  
  if (research.min_stars) {
    criteria.push(
      <span key="stars" className="inline-flex items-center gap-1 text-sm text-gray-600">
        <Star className="w-3 h-3" />
        {research.min_stars}+ stars
      </span>
    );
  }
  
  if (criteria.length === 0) {
    return <span className="text-sm text-gray-500">No filters</span>;
  }
  
  return <div className="flex flex-wrap gap-2">{criteria}</div>;
}

export function ResearchHistoryTable({ onViewDetails }: ResearchHistoryTableProps) {
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [statusFilter, setStatusFilter] = useState<VenueResearch['status'] | ''>('');

  // Query with polling for pending/processing jobs
  const { data, isLoading, error } = useQuery({
    queryKey: ['venue-research', page, pageSize, statusFilter],
    queryFn: () => VenueResearchService.listResearch(page, pageSize, statusFilter || undefined),
    refetchInterval: (query) => {
      // Poll every 5 seconds if there are any pending/processing jobs
      const hasActiveJobs = query.state.data?.items.some(
        (item) => item.status === 'pending' || item.status === 'processing'
      );
      return hasActiveJobs ? 5000 : false;
    },
    staleTime: 1000, // Consider data stale after 1 second
  });

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Research History</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-12">
            <Spinner size="lg" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Research History</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-12">
            <p className="text-error-600">Failed to load research history</p>
            <p className="text-sm text-gray-500 mt-2">{(error as Error).message}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const { items = [], pagination } = data || {};

  return (
    <Card className="mt-6">
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <CardTitle>Research History</CardTitle>
          
          {/* Status Filter */}
          <div className="flex items-center gap-2">
            <label htmlFor="status-filter" className="text-sm font-medium text-gray-700">
              Status:
            </label>
            <Select
              value={statusFilter}
              onValueChange={(value) => {
                setStatusFilter(value as VenueResearch['status'] | '');
                setPage(1); // Reset to first page when filtering
              }}
              className="w-40"
            >
              <option value="">All</option>
              <option value="pending">Pending</option>
              <option value="processing">Processing</option>
              <option value="completed">Completed</option>
              <option value="failed">Failed</option>
              <option value="cancelled">Cancelled</option>
            </Select>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {items.length === 0 ? (
          <div className="text-center py-12">
            <MapPin className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No research jobs found</p>
            <p className="text-sm text-gray-400 mt-2">
              {statusFilter ? 'Try changing the status filter' : 'Start a new research to see results here'}
            </p>
          </div>
        ) : (
          <>
            {/* Table */}
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      City & Criteria
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Results
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Duration
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Cost
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {items.map((research) => {
                    const statusBadge = getStatusBadge(research.status);
                    
                    return (
                      <tr key={research.id} className="hover:bg-gray-50">
                        {/* City & Criteria */}
                        <td className="px-6 py-4">
                          <div className="flex flex-col gap-1">
                            <div className="flex items-center gap-2 text-sm font-medium text-gray-900">
                              <MapPin className="w-4 h-4 text-gray-400" />
                              {research.search_city}
                              {/* Sequence Indicator */}
                              {research.total_sequences > 1 && (
                                <Badge variant="secondary" className="text-xs">
                                  <Repeat className="w-3 h-3 mr-1" />
                                  {research.search_sequence}/{research.total_sequences}
                                </Badge>
                              )}
                            </div>
                            <div className="ml-6">
                              {formatCriteria(research)}
                            </div>
                          </div>
                        </td>

                        {/* Status */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <Badge variant={statusBadge.variant}>
                            {statusBadge.label}
                          </Badge>
                        </td>

                        {/* Results */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          {research.status === 'completed' ? (
                            <div className="text-sm text-gray-900">
                              <span className="font-medium">{research.venues_found}</span> found,{' '}
                              <span className="text-success-600">{research.venues_created}</span> created,{' '}
                              <span className="text-primary-600">{research.venues_updated}</span> updated
                            </div>
                          ) : research.status === 'failed' ? (
                            <span className="text-sm text-error-600">Failed</span>
                          ) : research.status === 'cancelled' ? (
                            <div className="flex items-center gap-1 text-sm text-gray-500">
                              <Ban className="w-3 h-3" />
                              Cancelled
                            </div>
                          ) : (
                            <span className="text-sm text-gray-400">-</span>
                          )}
                        </td>

                        {/* Duration */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-1 text-sm text-gray-600">
                            <Clock className="w-3 h-3" />
                            {formatDuration(research.duration_seconds)}
                          </div>
                        </td>

                        {/* Cost */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          {research.llm_total_cost_usd ? (
                            <div className="flex items-center gap-1 text-sm text-gray-600">
                              <DollarSign className="w-3 h-3" />
                              {parseFloat(research.llm_total_cost_usd).toFixed(4)}
                            </div>
                          ) : (
                            <span className="text-sm text-gray-400">-</span>
                          )}
                        </td>

                        {/* Created */}
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDistanceToNow(new Date(research.created_at), { addSuffix: true })}
                        </td>

                        {/* Actions */}
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onViewDetails?.(research)}
                            disabled={!onViewDetails}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            View Details
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination && pagination.totalPages > 1 && (
              <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200">
                <div className="text-sm text-gray-700">
                  Showing <span className="font-medium">{(page - 1) * pageSize + 1}</span> to{' '}
                  <span className="font-medium">
                    {Math.min(page * pageSize, pagination.totalItems)}
                  </span>{' '}
                  of <span className="font-medium">{pagination.totalItems}</span> results
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
                    disabled={page === pagination.totalPages}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
